import React, { useState, useMemo, useEffect } from "react";
import { loadHistory, clearHistory } from "@/storage/routine";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trash2, Activity, TrendingUp, TrendingDown, Minus, Download, Target } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { BottomNav } from "@/components/BottomNav";
import { NameDialog } from "@/components/NameDialog";
import { BodyHeatmap } from "@/components/BodyHeatmap";
import { WeekHistory } from "@/components/WeekHistory";
import { loadWeeks } from "@/storage/weeks";
import { ExportDialog } from "@/components/ExportDialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useApp } from "@/context/AppContext";
import {
  loadWeeklyGoal,
  saveWeeklyGoal,
  seriesByGroup,
  groupIntensity,
} from "@/storage/weeklyGoal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

function MiniChart({ data, width = 280, height = 120 }) {
  const [tooltip, setTooltip] = useState(null);

  if (!data || data.length === 0) return null;

  const weights = data.map((d) => d.weight);
  const max = Math.max(...weights);
  const min = Math.min(...weights);
  const range = max - min || 1;
  const pad = 12;

  const points = data.map((d, i) => ({
    x: pad + (i / Math.max(data.length - 1, 1)) * (width - pad * 2),
    y: height - pad - ((d.weight - min) / range) * (height - pad * 2),
  }));

  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaD = `${pathD} L ${points[points.length - 1].x} ${height - pad} L ${points[0].x} ${height - pad} Z`;

  const handleMouseMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) * (width / rect.width);
    let nearestIdx = 0;
    let minDist = Infinity;
    points.forEach((p, i) => {
      const dist = Math.abs(p.x - mouseX);
      if (dist < minDist) { minDist = dist; nearestIdx = i; }
    });
    if (minDist < 28) {
      setTooltip({ ...points[nearestIdx], ...data[nearestIdx] });
    } else {
      setTooltip(null);
    }
  };

  const ttX = tooltip ? Math.min(Math.max(tooltip.x, pad + 40), width - pad - 40) : 0;
  const ttY = tooltip ? Math.max(tooltip.y - 46, pad) : 0;

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible cursor-crosshair"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setTooltip(null)}
    >
      {/* Grid lines */}
      <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad}
        stroke="hsl(var(--border))" strokeWidth={1} />
      <line x1={pad} y1={pad} x2={width - pad} y2={pad}
        stroke="hsl(var(--border))" strokeWidth={1} strokeDasharray="3,4" opacity={0.5} />

      {/* Area fill */}
      <defs>
        <linearGradient id={`grad-${data[0]?.date}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.2" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </linearGradient>
      </defs>
      {points.length > 1 && <path d={areaD} fill={`url(#grad-${data[0]?.date})`} />}
      {points.length > 1 && (
        <path d={pathD} stroke="hsl(var(--primary))" strokeWidth={2.5}
          fill="none" strokeLinecap="round" strokeLinejoin="round" />
      )}

      {/* Dots */}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y}
          r={i === points.length - 1 ? 5 : 3}
          fill={i === points.length - 1 ? "hsl(var(--success))" : "hsl(var(--primary))"}
          stroke="hsl(var(--background))" strokeWidth={2} />
      ))}

      {/* Labels */}
      <text x={pad} y={height - 1} fill="hsl(var(--muted-foreground))" fontSize={10}
        fontFamily="Outfit" fontWeight={500}>{min} kg</text>
      <text x={width - pad} y={height - 1} fill="hsl(var(--muted-foreground))" fontSize={10}
        fontFamily="Outfit" fontWeight={500} textAnchor="end">{max} kg</text>

      {/* Tooltip */}
      {tooltip && (
        <g>
          <line x1={tooltip.x} y1={pad} x2={tooltip.x} y2={height - pad}
            stroke="hsl(var(--primary))" strokeWidth={1} strokeDasharray="3,3" opacity={0.5} />
          <rect x={ttX - 48} y={ttY} width={96} height={40} rx={8}
            fill="hsl(var(--card))" stroke="hsl(var(--border))" strokeWidth={1} opacity={0.97} />
          <text x={ttX} y={ttY + 14} textAnchor="middle"
            fill="hsl(var(--foreground))" fontSize={12} fontWeight="700" fontFamily="Outfit">
            {tooltip.weight} kg
          </text>
          <text x={ttX} y={ttY + 28} textAnchor="middle"
            fill="hsl(var(--muted-foreground))" fontSize={9} fontFamily="Outfit">
            {new Date(tooltip.date).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
          </text>
          <text x={ttX} y={ttY + 38} textAnchor="middle"
            fill="hsl(var(--muted-foreground))" fontSize={8} fontFamily="Outfit" opacity={0.7}>
            {new Date(tooltip.date).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
          </text>
        </g>
      )}
    </svg>
  );
}

function WeeklySummary() {
  const { state, totals } = useApp();
  const [goal, setGoal] = useState(loadWeeklyGoal);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const counts = useMemo(
    () => (state ? seriesByGroup(state.routine, state.progress) : {}),
    [state]
  );
  const intensity = useMemo(() => groupIntensity(counts), [counts]);

  const done = totals?.done ?? 0;
  const pct = goal > 0 ? Math.min(100, Math.round((done / goal) * 100)) : 0;

  // Referencia contra la que medirse mientras la semana está en curso.
  const previous = useMemo(() => loadWeeks()[0] || null, []);

  const commitGoal = () => {
    const value = parseInt(draft, 10);
    if (!isNaN(value) && value > 0) {
      saveWeeklyGoal(value);
      setGoal(value);
      toast.success("Meta actualizada");
    }
    setEditing(false);
  };

  const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-4">
      <Card className="glass-surface rounded-3xl border-0 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-foreground font-bold text-lg tabular-nums">
              {done}/{goal}{" "}
              <span className="text-sm font-medium text-muted-foreground">series</span>
            </p>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-semibold mt-0.5">
              Meta semanal
            </p>
          </div>

          {editing ? (
            <div className="flex gap-1.5">
              <Input
                type="number"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && commitGoal()}
                autoFocus
                className="h-9 w-20 rounded-xl bg-secondary/30 border-border/40 text-center font-bold"
              />
              <Button onClick={commitGoal} className="h-9 px-3 rounded-xl bg-primary text-primary-foreground text-xs font-bold">
                OK
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              onClick={() => {
                setDraft(String(goal));
                setEditing(true);
              }}
              className="h-9 px-3 rounded-xl glass-surface text-xs font-semibold text-muted-foreground gap-1.5"
            >
              <Target className="h-3.5 w-3.5" />
              Meta
            </Button>
          )}
        </div>

        <div className="h-2 rounded-full bg-secondary/50 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>

        {previous && (
          <p className="text-[11px] text-muted-foreground mt-3 pt-3 border-t border-border/30">
            Semana {previous.week}:{" "}
            <span className="text-foreground/80 font-semibold tabular-nums">
              {previous.totalSets} series
            </span>{" "}
            ·{" "}
            <span className="text-foreground/80 font-semibold tabular-nums">
              {previous.totalVolume?.toLocaleString("es-ES")} kg
            </span>
            <span className="text-muted-foreground/60"> · el detalle, en Semanas</span>
          </p>
        )}
      </Card>

      <Card className="glass-surface rounded-3xl border-0 p-5 space-y-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Volumen por músculo
        </p>
        <BodyHeatmap intensity={intensity} counts={counts} />

        {ranked.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {ranked.map(([group, count]) => (
              <Badge
                key={group}
                className="bg-secondary/50 text-foreground/80 border-0 rounded-full text-[10px] font-semibold"
              >
                {group} · {count}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-center text-xs text-muted-foreground py-2">
            Marca series como completadas para ver qué músculos estás trabajando.
          </p>
        )}
      </Card>
    </div>
  );
}

export default function HistoryPage() {
  const [history, setHistory] = useState({});
  const [weeks, setWeeks] = useState([]);
  const [confirmClear, setConfirmClear] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    setHistory(loadHistory());
    setWeeks(loadWeeks());
  }, []);

  const entries = useMemo(() => {
    return Object.entries(history).filter(([, v]) => v && v.length > 0);
  }, [history]);

  const handleClear = () => {
    clearHistory();
    setHistory({});
    setConfirmClear(false);
    toast.success("Historial borrado");
  };

  return (
    <div className="min-h-screen bg-background relative pb-24">
      <div
        className="pointer-events-none fixed top-0 left-0 right-0 h-[400px] opacity-50"
        style={{ background: "radial-gradient(ellipse at 50% 0%, hsl(199 89% 56% / 0.06), transparent 70%)" }}
      />

      <div className="relative z-10 mx-auto max-w-2xl px-4 sm:px-6 pb-16 pt-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="flex-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-[0.1em]">Tu evolución</p>
            <h1 className="text-2xl font-bold text-foreground tracking-tight -mt-0.5">Progreso</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Exportar no depende del historial de pesos: aunque no haya
                ninguno, el informe lleva los ciclos, las medidas y la rutina. */}
            <Button variant="ghost" size="icon" onClick={() => setExportOpen(true)}
              aria-label="Exportar mis datos"
              className="h-11 w-11 rounded-full glass-surface hover:bg-primary/10 text-primary">
              <Download className="h-4 w-4" />
            </Button>
            {entries.length > 0 && (
              <Button variant="ghost" size="icon" onClick={() => setConfirmClear(true)}
                aria-label="Borrar historial"
                className="h-11 w-11 rounded-full glass-surface hover:bg-destructive/10 text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <ExportDialog open={exportOpen} onOpenChange={setExportOpen} />

        <Tabs defaultValue="resumen" className="w-full min-w-0">
          <TabsList className="w-full bg-secondary/30 rounded-xl h-10 p-1 mb-5">
            <TabsTrigger
              value="resumen"
              className="flex-1 rounded-lg text-xs font-semibold data-[state=active]:bg-primary/15 data-[state=active]:text-primary"
            >
              Resumen
            </TabsTrigger>
            <TabsTrigger
              value="semanas"
              className="flex-1 rounded-lg text-xs font-semibold data-[state=active]:bg-primary/15 data-[state=active]:text-primary"
            >
              Semanas
            </TabsTrigger>
            <TabsTrigger
              value="ejercicios"
              className="flex-1 rounded-lg text-xs font-semibold data-[state=active]:bg-primary/15 data-[state=active]:text-primary"
            >
              Ejercicios
            </TabsTrigger>
          </TabsList>

          <TabsContent value="resumen" className="mt-0 min-w-0">
            <WeeklySummary />
          </TabsContent>

          <TabsContent value="semanas" className="mt-0 min-w-0">
            <WeekHistory weeks={weeks} />
          </TabsContent>

          <TabsContent value="ejercicios" className="mt-0 min-w-0">
        <AnimatePresence mode="wait">
          {entries.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center py-16 px-6 glass-surface rounded-3xl border-dashed mt-8"
            >
              <Activity className="h-8 w-8 text-muted-foreground/50 mb-4" />
              <p className="text-foreground/80 font-semibold text-base">Aún no hay historial</p>
              <p className="text-muted-foreground text-sm mt-2 text-center max-w-xs leading-relaxed">
                Introduce el peso en cada serie y márcala como completada para registrar tu progreso.
              </p>
            </motion.div>
          ) : (
            <div className="space-y-4">
              {entries.map(([name, data], idx) => {
                const weights = data.map((d) => d.weight);
                const last = weights[weights.length - 1];
                const first = weights[0];
                const delta = last - first;
                const deltaPct = first > 0 ? (delta / first) * 100 : 0;

                return (
                  <motion.div
                    key={name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.06, duration: 0.3 }}
                  >
                    <Card className="glass-surface rounded-3xl overflow-hidden p-5 border-0">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-foreground font-bold text-lg tracking-tight truncate">{name}</h3>
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-muted-foreground text-xs">
                              {data.length} registro{data.length === 1 ? "" : "s"}
                            </p>
                            <span className="text-muted-foreground/30 text-xs">·</span>
                            <p className="text-muted-foreground text-xs tabular-nums">
                              {new Date(data[data.length - 1].date).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
                              {" · "}
                              {new Date(data[data.length - 1].date).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 ml-3">
                          <span className="text-foreground font-bold text-xl tabular-nums">{last} <span className="text-sm font-medium text-muted-foreground">kg</span></span>
                          <Badge
                            variant="secondary"
                            className={`rounded-full text-[11px] font-semibold px-2 py-0.5 gap-1 border-0 ${
                              delta > 0
                                ? "bg-success/15 text-success"
                                : delta < 0
                                ? "bg-destructive/15 text-destructive"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {delta > 0 ? <TrendingUp className="h-3 w-3" /> : delta < 0 ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                            {delta > 0 ? "+" : ""}{deltaPct.toFixed(0)}%
                          </Badge>
                        </div>
                      </div>
                      <div className="px-1 mt-3">
                        <MiniChart data={data} />
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </AnimatePresence>
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirm clear dialog */}
      <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
        <DialogContent className="glass-surface rounded-3xl border-0 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Borrar historial</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              ¿Seguro que quieres borrar todo el historial? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2">
            <Button variant="ghost" onClick={() => setConfirmClear(false)}
              className="flex-1 rounded-xl glass-surface text-foreground/80 hover:text-foreground hover:bg-secondary/60">
              Cancelar
            </Button>
            <Button onClick={handleClear}
              className="flex-1 rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Borrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
      <NameDialog />
    </div>
  );
}
